import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import Layout from '@/components/Layout';
import { PageHeader } from '@/components/ui/PageHeader';
import { RecommendedJobsCard, RecentActivityCard } from '@/components/dashboard';
import { InterviewReadinessCard } from '@/components/dashboard/InterviewReadinessCard';
import { TransitionWindowStrip } from '@/components/dashboard/TransitionWindowStrip';
import { OneNextActionCard } from '@/components/dashboard/OneNextActionCard';
import { MilitaryTranslationPreview } from '@/components/dashboard/MilitaryTranslationPreview';
import { ActiveContinuityCard } from '@/components/dashboard/ActiveContinuityCard';
import { getDashboardState, DASHBOARD_STATE } from '@/lib/dashboardState';
import { FileText, Plus, Eye, EyeOff, Users, Activity, MapPin, UserPlus, Trash2, Shield, Edit2, Key, Search, ToggleLeft, ToggleRight, BarChart3, TrendingUp, Briefcase, MessageSquare, CheckCircle, Home, RotateCcw, AlertTriangle, Clock, Loader2, Building2, Wrench, Settings, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import AdminOverview from '@/pages/admin/AdminOverview';
import AdminOrganizations from '@/pages/admin/AdminOrganizations';
import AdminActivity from '@/pages/admin/AdminActivity';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';
import AdminSessions from '@/pages/admin/AdminSessions';

const ADMIN_SECTIONS = [
  { key: 'overview', label: 'Overview' },
  { key: 'users', label: 'Users' },
  { key: 'regions', label: 'Regions' },
  { key: 'organizations', label: 'Organizations' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'activity', label: 'Activity' },
  { key: 'operations', label: 'Operations' },
  { key: 'administration', label: 'Administration' },
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Job Seeker Dashboard Component
function JobSeekerDashboard({ user, resume, navigate, jobs, nearDutyStation = [], unreadCount, conversations = [], applications = [], savedJobs = [], loadingJobs }) {
  // Dashboard State Architecture — Steps 1, 2, 6.
  // Derive a UX state (1..4) per
  // `/app/memory/UX_DASHBOARD_STATE_ARCHITECTURE.md`. State signals are
  // computed from already-loaded data (no extra fetch, no persistence).
  //
  // For Step 6 we now feed REAL conversations + applications into the
  // derivation, so the State 4 ("Active Market") branch reflects actual
  // engagement instead of the prior unread-count approximation.
  const [twsTick, setTwsTick] = useState(0); // re-derive when the transition date changes
  // Canonical transition date comes from the user account (`transition_date`).
  // Tracked locally so the dashboard state re-derives immediately after a save.
  const [transitionDate, setTransitionDate] = useState(user?.transition_date || null);
  useEffect(() => {
    setTransitionDate(user?.transition_date || null);
  }, [user?.transition_date]);
  const handleTransitionChange = (val) => {
    setTransitionDate(val || null);
    setTwsTick((t) => t + 1);
  };
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'st_transition_date') setTwsTick((t) => t + 1);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const dashboardState = getDashboardState({
    resume,
    savedJobs: Array.isArray(savedJobs) ? savedJobs : [],
    applications,
    conversations,
    // Feed the canonical account transition date so intent signal reflects
    // the backend value (falls back to any legacy localStorage value when null).
    localStorageDate: transitionDate ?? undefined,
    _tick: twsTick, // eslint-disable-line no-unused-vars
  });

  // Active-engagement counts surfaced quietly in the State 4 spine.
  // Conversations are counted in full (any open thread is "in motion").
  // Applications exclude any final/terminal state so we don't surface
  // long-completed records as "in motion".
  const activeConversationCount = Array.isArray(conversations) ? conversations.length : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex items-center gap-4" data-testid="dashboard-heading">
        <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center flex-shrink-0">
          <FileText className="w-6 h-6 text-brand" strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            {`My Transition, ${user?.name?.split(' ')[0] || user?.name || ''}`.trim()}
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-0.5">Your transition at a glance.</p>
        </div>
      </div>

      {/* Dashboard State Architecture (Step 3) — State 1 anchor.
            Surfaces a single calm next-action card for brand-new users. */}
      {dashboardState === DASHBOARD_STATE.JUST_ARRIVED && (
        <OneNextActionCard onPrimaryAction={() => navigate('/resume-builder')} />
      )}

      {/* Dashboard State Architecture (Step 2) — gate the Transition Window:
            State 1 (Just Arrived) — hidden completely.
            State 2 (Early Build)  — minimized one-line prompt.
            State 3 / 4            — full strip (Phase 1B behavior preserved). */}
      {/* Dashboard State Architecture (Steps 2 + 5) — UNIFIED TRANSITION
          WORKSPACE. The Window strip and the Military Translation Preview
          read as ONE contextual area, not two stacked strips:
            State 1 (Just Arrived) — workspace hidden entirely (handled below).
            State 2 (Early Build)  — minimized window prompt + prominent MTP.
            State 3 (Transitioning) — full strip + compact MTP.
            State 4 (Active Market) — full strip only (MTP hidden for now). */}
      {dashboardState === DASHBOARD_STATE.EARLY_BUILD && (
        <section className="mb-5 space-y-3" data-testid="transition-workspace">
          <TransitionWindowStrip variant="minimized" serverDate={transitionDate} onChange={handleTransitionChange} />
          <MilitaryTranslationPreview
            resume={resume}
            isResumeComplete={resume?.is_complete === true}
          />
        </section>
      )}
      {(dashboardState === DASHBOARD_STATE.TRANSITIONING ||
        dashboardState === DASHBOARD_STATE.ACTIVE_MARKET) && (
        <section className="mb-5 space-y-3" data-testid="transition-workspace">
          <TransitionWindowStrip serverDate={transitionDate} onChange={handleTransitionChange} />
          {dashboardState === DASHBOARD_STATE.TRANSITIONING && (
            <MilitaryTranslationPreview
              resume={resume}
              compact
              isResumeComplete={resume?.is_complete === true}
            />
          )}
        </section>
      )}

      {/* Main Grid — hidden entirely in State 1 (Just Arrived) so the page
          reads as a calm intentional starting point, not a half-loaded
          dashboard. Tighter gap + no top margin so it flows directly out
          of the transition workspace above. */}
      {dashboardState !== DASHBOARD_STATE.JUST_ARRIVED && (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left Column - Primary Actions */}
        <div className="lg:col-span-3 space-y-6">
          <Card className="p-6 border border-slate-200 shadow-sm rounded-2xl" data-testid="resume-status-card">
            <div className="flex gap-4">
              <div className="w-14 h-14 rounded-xl bg-brand-light flex items-center justify-center flex-shrink-0">
                <FileText className="w-7 h-7 text-brand" strokeWidth={1.6} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-bold text-slate-900 mb-1">Resume Status</h3>
                <p className="text-base text-slate-700 mb-4 flex items-center gap-1.5">
                  {resume?.is_complete ? (
                    <>Complete <CheckCircle className="w-4 h-4 text-brand" /></>
                  ) : (
                    'In Progress'
                  )}
                </p>
                <Button
                  data-testid="continue-resume-btn"
                  onClick={() => navigate('/resume-builder')}
                  className="w-full bg-brand hover:bg-brand-hover text-white font-semibold py-5"
                >
                  {resume?.is_complete ? 'Open my resume' : 'Continue Building'}
                </Button>
                <button
                  data-testid="manage-visibility-link"
                  type="button"
                  onClick={() => navigate('/profile/visibility')}
                  className="mt-3 text-sm text-slate-500 hover:text-brand underline underline-offset-2"
                >
                  Manage visibility
                </button>
              </div>
            </div>
          </Card>

          {/* Best Matches — highest canonical .4 overall (geography ignored) */}
          <RecommendedJobsCard
            jobs={jobs}
            navigate={navigate}
            loading={loadingJobs}
            title={
              dashboardState === DASHBOARD_STATE.EARLY_BUILD && !(jobs?.[0]?.final_ranked_score != null)
                ? 'Explore opportunities'
                : 'Best Matches'
            }
            subtitle="Your highest overall Match opportunities."
            testId="best-matches-card"
          />

          {/* Careers Near Your Duty Station — credible roles within 50mi of the seeker's base */}
          {jobs?.[0]?.final_ranked_score != null && (
            <RecommendedJobsCard
              jobs={nearDutyStation}
              navigate={navigate}
              loading={loadingJobs}
              title="Careers Near Your Duty Station"
              subtitle="Credible opportunities near your current installation that may let you stay in the area after transition. Distance affects discovery, not your Match score."
              emptyText="No credible roles near your duty station right now"
              emptySubtext="Browse the full job board to explore more opportunities"
              testId="careers-near-duty-station-card"
            />
          )}
        </div>

        {/* Right Column - Prepare + In Motion + Quiet Updates */}
        <div className="lg:col-span-2 space-y-6">
          <InterviewReadinessCard navigate={navigate} />
          {dashboardState === DASHBOARD_STATE.ACTIVE_MARKET && (
            <ActiveContinuityCard
              conversationCount={activeConversationCount}
              applications={applications}
              navigate={navigate}
            />
          )}
          <RecentActivityCard 
            unreadMessagesCount={unreadCount} 
            newJobsCount={jobs?.length || 0}
            navigate={navigate} 
          />
        </div>
      </div>
      )}
    </div>
  );
}

// Admin Dashboard Component
function AdminDashboardContent({ user }) {
  const navigate = useNavigate();
  const [adminTab, setAdminTab] = useState('overview');
  const [activityView, setActivityView] = useState('events');
  const [roleFilter, setRoleFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionalManagers, setRegionalManagers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchRegion, setSearchRegion] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  
  // Dialog states
  const [createRegionOpen, setCreateRegionOpen] = useState(false);
  const [editRegionOpen, setEditRegionOpen] = useState(false);
  const [createManagerOpen, setCreateManagerOpen] = useState(false);
  const [editManagerOpen, setEditManagerOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  
  // Form states
  const [newRegion, setNewRegion] = useState({ name: '', type: 'County', state: '', description: '', is_active: true });
  const [newManager, setNewManager] = useState({ name: '', email: '', password: '', region_id: '' });
  const [newPassword, setNewPassword] = useState('');

  // User lifecycle states
  const [usersView, setUsersView] = useState('active'); // 'active' | 'trashed'
  const [trashedUsers, setTrashedUsers] = useState([]);
  const [trashConfirmOpen, setTrashConfirmOpen] = useState(false);
  const [purgeConfirmOpen, setPurgeConfirmOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [purgeConfirmText, setPurgeConfirmText] = useState('');
  const [userActionLoading, setUserActionLoading] = useState(false);
  
  // External Job Sync state
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncCleanup, setSyncCleanup] = useState(false);
  const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
  const [syncConfirmPhrase, setSyncConfirmPhrase] = useState('');
  const [syncFullConfirmOpen, setSyncFullConfirmOpen] = useState(false);
  const [syncFullConfirmPhrase, setSyncFullConfirmPhrase] = useState('');

  useEffect(() => {
    fetchAllData();
  }, [searchRegion, showActiveOnly]);

  const fetchAllData = async () => {
    try {
      const params = new URLSearchParams();
      if (searchRegion) params.append('search', searchRegion);
      if (showActiveOnly) params.append('active_only', 'true');
      
      const [usersRes, logsRes, regionsRes, managersRes, analyticsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/logs?limit=50`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/regions?${params.toString()}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/regional-managers`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/analytics/overview`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      setUsers(usersRes.data);
      setLogs(logsRes.data);
      setRegions(regionsRes.data);
      setRegionalManagers(managersRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load some data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrashedUsers = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/users/trashed`, { withCredentials: true });
      setTrashedUsers(r.data);
    } catch {}
  };

  useEffect(() => {
    if (usersView === 'trashed') fetchTrashedUsers();
  }, [usersView]);

  const handleTrashUser = async () => {
    if (!targetUser) return;
    setUserActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/trash`, { user_id: targetUser.user_id }, { withCredentials: true });
      toast.success(`${targetUser.email} moved to trash`);
      setTrashConfirmOpen(false);
      setTargetUser(null);
      fetchAllData();
      fetchTrashedUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to trash user');
    } finally { setUserActionLoading(false); }
  };

  const handleRestoreUser = async (u) => {
    setUserActionLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/users/restore`, { user_id: u.user_id }, { withCredentials: true });
      toast.success(`${u.email} restored`);
      fetchAllData();
      fetchTrashedUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore user');
    } finally { setUserActionLoading(false); }
  };

  const handlePurgeUser = async () => {
    if (!targetUser || purgeConfirmText !== 'DELETE') return;
    setUserActionLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/users/purge`, {
        user_id: targetUser.user_id, confirm: 'DELETE',
      }, { withCredentials: true });
      toast.success(`${targetUser.email} permanently deleted`);
      setPurgeConfirmOpen(false);
      setTargetUser(null);
      setPurgeConfirmText('');
      fetchTrashedUsers();
      fetchAllData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally { setUserActionLoading(false); }
  };

  // Region CRUD handlers
  const handleCreateRegion = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/admin/regions/create`, newRegion, { withCredentials: true });
      toast.success('Region created successfully');
      setNewRegion({ name: '', type: 'County', state: '', description: '', is_active: true });
      setCreateRegionOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create region');
    }
  };

  const handleUpdateRegion = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${BACKEND_URL}/api/admin/regions/${selectedRegion.region_id}`, selectedRegion, { withCredentials: true });
      toast.success('Region updated successfully');
      setEditRegionOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update region');
    }
  };

  const handleDeleteRegion = async (regionId) => {
    if (!confirm('Are you sure you want to delete this region?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/regions/${regionId}`, { withCredentials: true });
      toast.success('Region deleted successfully');
      fetchAllData();
    } catch (error) {
      console.error('Delete region error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to delete region');
    }
  };

  const handleToggleRegionActive = async (region) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/regions/${region.region_id}`, 
        { is_active: !region.is_active }, 
        { withCredentials: true }
      );
      toast.success(`Region ${region.is_active ? 'deactivated' : 'activated'}`);
      fetchAllData();
    } catch (error) {
      console.error('Toggle region error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to update region status');
    }
  };

  // Regional Manager CRUD handlers
  const handleCreateManager = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/regional-managers/create`, newManager, { withCredentials: true });
      toast.success(`Regional manager created! Email: ${newManager.email}`);
      setNewManager({ name: '', email: '', password: '', region_id: '' });
      setCreateManagerOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create regional manager');
    }
  };

  const handleUpdateManager = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${BACKEND_URL}/api/admin/regional-managers/${selectedManager.admin_id}`, selectedManager, { withCredentials: true });
      toast.success('Regional manager updated');
      setEditManagerOpen(false);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/admin/regional-managers/${selectedManager.admin_id}/reset-password`, 
        { new_password: newPassword }, 
        { withCredentials: true }
      );
      toast.success('Password reset successfully');
      setResetPasswordOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeleteManager = async (adminId) => {
    if (!confirm('Are you sure you want to remove this regional manager?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/regional-managers/${adminId}`, { withCredentials: true });
      toast.success('Regional manager removed');
      fetchAllData();
    } catch (error) {
      console.error('Delete manager error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed to remove regional manager');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // External Job Sync handlers
  const handleSyncDryRun = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/external-jobs/lowcountry/sync`, {
        dry_run: true, limit: 15, max_pages: 3, sync_mode: 'batch',
        cleanup_existing_employer_data: syncCleanup,
        cleanup_confirmation_phrase: '',
      }, { withCredentials: true });
      setSyncResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync dry run failed');
    } finally { setSyncLoading(false); }
  };

  const handleSyncLive = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/external-jobs/lowcountry/sync`, {
        dry_run: false, limit: 15, max_pages: 3, sync_mode: 'batch',
        cleanup_existing_employer_data: syncCleanup,
        cleanup_confirmation_phrase: syncCleanup ? syncConfirmPhrase : '',
      }, { withCredentials: true });
      setSyncResult(res.data);
      if (res.data.inserted_count > 0 || res.data.updated_count > 0) {
        toast.success(`Imported ${res.data.inserted_count} new, updated ${res.data.updated_count}`);
      }
      if (res.data.cleanup_summary?.deleted_counts?.employers > 0) {
        toast.success(`Cleanup: removed ${res.data.cleanup_summary.deleted_counts.employers} demo employers`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally { setSyncLoading(false); setSyncConfirmOpen(false); setSyncConfirmPhrase(''); }
  };

  const handleSyncFullDryRun = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/external-jobs/lowcountry/sync`, {
        dry_run: true, sync_mode: 'full', import_limit: 500, max_pages: 50,
        cleanup_existing_employer_data: syncCleanup,
        cleanup_confirmation_phrase: '',
      }, { withCredentials: true });
      setSyncResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Full dry run failed');
    } finally { setSyncLoading(false); }
  };

  const handleSyncFullLive = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/external-jobs/lowcountry/sync`, {
        dry_run: false, sync_mode: 'full', import_limit: 500, max_pages: 50,
        cleanup_existing_employer_data: syncCleanup,
        cleanup_confirmation_phrase: syncCleanup ? syncConfirmPhrase : '',
      }, { withCredentials: true });
      setSyncResult(res.data);
      if (res.data.inserted_count > 0 || res.data.updated_count > 0) {
        toast.success(`Full import: ${res.data.inserted_count} new, ${res.data.updated_count} updated`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Full import failed');
    } finally { setSyncLoading(false); setSyncFullConfirmOpen(false); setSyncFullConfirmPhrase(''); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader 
        icon={Shield}
        title="Admin Dashboard"
        subtitle="System management and regional administration"
      />

      {/* Section navigation (unified Admin information architecture) */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-3" data-testid="admin-section-nav">
        {ADMIN_SECTIONS.map(s => (
          <button
            key={s.key}
            data-testid={`admin-nav-${s.key}`}
            onClick={() => setAdminTab(s.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${adminTab === s.key ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Legacy at-a-glance stats retained but hidden — Overview section supersedes */}
      <div className="hidden">
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-slate-900">{users.length}</p>
            </div>
            <Users className="w-10 h-10 text-brand" strokeWidth={1.5} />
          </div>
        </Card>
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Regions</p>
              <p className="text-3xl font-bold text-slate-900">{regions.length}</p>
            </div>
            <MapPin className="w-10 h-10 text-purple-600" strokeWidth={1.5} />
          </div>
        </Card>
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Regional Managers</p>
              <p className="text-3xl font-bold text-slate-900">{regionalManagers.length}</p>
            </div>
            <Shield className="w-10 h-10 text-green-600" strokeWidth={1.5} />
          </div>
        </Card>
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 mb-1">Recent Activity</p>
              <p className="text-3xl font-bold text-slate-900">{logs.length}</p>
            </div>
            <Activity className="w-10 h-10 text-brand" strokeWidth={1.5} />
          </div>
        </Card>
      </div>

      {/* (Homepage Editor moved to the Administration section below) */}

      {/* External Job Sync — shown under the Operations section */}
      {adminTab === 'operations' && (
      <Card className="p-5 border border-slate-200 shadow-sm mb-6" data-testid="external-job-sync-card">
        <h3 className="text-sm font-bold text-slate-900 mb-1">External Job Sync</h3>
        <p className="text-xs text-slate-500 mb-4">Manual sync for syndicated Lowcountry Jobs Now postings. Dry run first, then import up to 15 jobs.</p>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <Button data-testid="sync-dry-run-btn" size="sm" variant="outline" onClick={handleSyncDryRun} disabled={syncLoading} className="text-xs">
            {syncLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />} Dry Run (15)
          </Button>
          <Button data-testid="sync-import-btn" size="sm" onClick={() => syncCleanup ? setSyncConfirmOpen(true) : handleSyncLive()} disabled={syncLoading} className="bg-brand hover:bg-brand-hover text-white text-xs">
            {syncLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Briefcase className="w-3 h-3 mr-1" />} Import 15
          </Button>
          <span className="text-slate-300">|</span>
          <Button data-testid="sync-full-dry-run-btn" size="sm" variant="outline" onClick={handleSyncFullDryRun} disabled={syncLoading} className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50">
            {syncLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Search className="w-3 h-3 mr-1" />} Dry Run Full
          </Button>
          <Button data-testid="sync-full-import-btn" size="sm" onClick={() => setSyncFullConfirmOpen(true)} disabled={syncLoading} className="bg-amber-600 hover:bg-amber-700 text-white text-xs">
            {syncLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Briefcase className="w-3 h-3 mr-1" />} Import All
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer" data-testid="sync-cleanup-checkbox">
            <input type="checkbox" checked={syncCleanup} onChange={(e) => setSyncCleanup(e.target.checked)} className="rounded border-slate-300" />
            Remove demo employer data (except richy@rich.com)
          </label>
        </div>
        {syncResult && (
          <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs space-y-2 max-h-[500px] overflow-y-auto" data-testid="sync-result-panel">
            <div className="flex flex-wrap gap-x-4 gap-y-1 font-medium text-slate-700">
              <span>Mode: {syncResult.dry_run ? 'Dry Run' : 'LIVE'} ({syncResult.sync_mode})</span>
              <span>Pages: {syncResult.pages_with_jobs}/{syncResult.pages_attempted}</span>
              <span>Parsed: {syncResult.total_parsed_raw}</span>
              <span>Ready: {syncResult.total_ready_to_import}</span>
              {!syncResult.dry_run && <span className="text-emerald-700">Inserted: {syncResult.inserted_count} | Updated: {syncResult.updated_count}</span>}
              <span>Skipped: {syncResult.skipped_count}</span>
              <span className="text-slate-400">Deletes: {syncResult.automatic_deletes ?? 0} | Stale: {syncResult.stale_detection_run ? 'yes' : 'no'}</span>
            </div>

            {/* Synthetic Employer Status */}
            {syncResult.synthetic_employer && (
              <div className="p-2 bg-white rounded border border-slate-200">
                <p className="font-semibold text-slate-700 mb-0.5">Synthetic Employer</p>
                <div className="flex flex-wrap gap-x-4 text-slate-500">
                  <span>{syncResult.synthetic_employer.employer_name}</span>
                  <span>ID: {syncResult.synthetic_employer.employer_id}</span>
                  <span>Type: {syncResult.synthetic_employer.source_type}</span>
                  <span className={syncResult.synthetic_employer.action === 'existing' ? 'text-slate-600' : 'text-emerald-600'}>
                    Action: {syncResult.synthetic_employer.action}
                  </span>
                </div>
              </div>
            )}

            {/* Location Detail Table */}
            {syncResult.location_details?.length > 0 && (
              <div className="p-2 bg-white rounded border border-slate-200">
                <p className="font-semibold text-slate-700 mb-1">Location Resolution</p>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-100">
                      <th className="pb-1 pr-2">Location</th>
                      <th className="pb-1 pr-2">Lat</th>
                      <th className="pb-1 pr-2">Lng</th>
                      <th className="pb-1 pr-2">Source</th>
                      <th className="pb-1 pr-2">Jobs</th>
                      <th className="pb-1">Raw Examples</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncResult.location_details.map((loc, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-0.5 pr-2 font-medium text-slate-700">{loc.normalized_location}</td>
                        <td className="py-0.5 pr-2 text-slate-500 tabular-nums">{loc.latitude?.toFixed(4) || '—'}</td>
                        <td className="py-0.5 pr-2 text-slate-500 tabular-nums">{loc.longitude?.toFixed(4) || '—'}</td>
                        <td className="py-0.5 pr-2">
                          <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                            loc.resolution_source === 'hard_map' ? 'bg-emerald-50 text-emerald-700' :
                            loc.resolution_source === 'cache' ? 'bg-sky-50 text-sky-700' :
                            loc.resolution_source === 'geoapify' ? 'bg-amber-50 text-amber-700' :
                            'bg-red-50 text-red-700'
                          }`}>{loc.resolution_source}</span>
                        </td>
                        <td className="py-0.5 pr-2 text-slate-600 font-medium">{loc.job_count}</td>
                        <td className="py-0.5 text-slate-400 truncate max-w-[150px]">{loc.raw_examples?.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {syncResult.location_summary && (
              <div className="text-slate-500">
                Summary: {syncResult.location_summary.hard_mapped_count} hard-mapped, {syncResult.location_summary.cache_hit_count} cached, {syncResult.location_summary.geoapify_called_count} Geoapify, {syncResult.location_summary.failed_resolution_count} failed
              </div>
            )}
            {syncResult.cleanup_summary && (
              <div className={`p-2 rounded ${syncResult.cleanup_summary.cleanup_blocked ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                <p className="font-semibold">Cleanup: {syncResult.cleanup_summary.cleanup_blocked ? 'BLOCKED' : (syncResult.cleanup_summary.cleanup_dry_run ? 'Preview' : 'Executed')}</p>
                {syncResult.cleanup_summary.blocked_reason && <p>{syncResult.cleanup_summary.blocked_reason}</p>}
                {syncResult.cleanup_summary.delete_preview && (
                  <p>Would delete: {syncResult.cleanup_summary.delete_preview.employers} employers, {syncResult.cleanup_summary.delete_preview.jobs} jobs, {syncResult.cleanup_summary.delete_preview.job_applications} apps</p>
                )}
                {syncResult.cleanup_summary.protected_preview && (
                  <p>Protected: {syncResult.cleanup_summary.protected_preview.employers_preserved} employers, {syncResult.cleanup_summary.protected_preview.jobs_preserved} jobs</p>
                )}
                {syncResult.cleanup_summary.deleted_counts && Object.keys(syncResult.cleanup_summary.deleted_counts).length > 0 && (
                  <p className="font-semibold">Deleted: {JSON.stringify(syncResult.cleanup_summary.deleted_counts)}</p>
                )}
              </div>
            )}

            {/* Job Preview Table */}
            {syncResult.jobs_preview?.length > 0 && (
              <div className="p-2 bg-white rounded border border-slate-200">
                <p className="font-semibold text-slate-700 mb-1">Jobs ({syncResult.jobs_preview.length})</p>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-slate-100">
                      <th className="pb-1 pr-2">Title</th>
                      <th className="pb-1 pr-2">Location</th>
                      <th className="pb-1 pr-2">Lat/Lng</th>
                      <th className="pb-1 pr-2">Source</th>
                      <th className="pb-1">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncResult.jobs_preview.map((j, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-0.5 pr-2 text-slate-700 truncate max-w-[200px]">{j.title}</td>
                        <td className="py-0.5 pr-2 text-slate-500">{j.location_normalized}</td>
                        <td className="py-0.5 pr-2 text-slate-400 tabular-nums">{j.latitude?.toFixed(2)}, {j.longitude?.toFixed(2)}</td>
                        <td className="py-0.5 pr-2">
                          <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                            j.location_resolution_source === 'hard_map' ? 'bg-emerald-50 text-emerald-700' :
                            j.location_resolution_source === 'cache' ? 'bg-sky-50 text-sky-700' :
                            'bg-amber-50 text-amber-700'
                          }`}>{j.location_resolution_source}</span>
                        </td>
                        <td className="py-0.5 text-emerald-600">{j.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {syncResult.warnings?.length > 0 && (
              <div className="text-amber-700">Warnings: {syncResult.warnings.join(', ')}</div>
            )}
          </div>
        )}
      </Card>
      )}

      {/* Sync Confirmation Modal (batch + cleanup) */}
      <Dialog open={syncConfirmOpen} onOpenChange={setSyncConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700">Confirm Live Import + Cleanup</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-3">
            This will delete employer/demo data except the account belonging to richy@rich.com, then import up to 15 Lowcountry Jobs Now postings.
          </p>
          <p className="text-xs font-semibold text-slate-700 mb-2">Type exactly: <code className="bg-red-50 px-1 py-0.5 text-red-700">DELETE DEMO EMPLOYER DATA EXCEPT RICHY</code></p>
          <Input data-testid="sync-confirm-input" value={syncConfirmPhrase} onChange={(e) => setSyncConfirmPhrase(e.target.value)} placeholder="Type confirmation phrase..." className="mb-3" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSyncConfirmOpen(false)}>Cancel</Button>
            <Button data-testid="sync-confirm-execute-btn" size="sm" onClick={handleSyncLive} disabled={syncConfirmPhrase !== 'DELETE DEMO EMPLOYER DATA EXCEPT RICHY' || syncLoading} className="bg-red-600 hover:bg-red-700 text-white">
              {syncLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Execute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Import Confirmation Modal */}
      <Dialog open={syncFullConfirmOpen} onOpenChange={setSyncFullConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-700">Confirm Full Lowcountry Import</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600 mb-3">
            Import all available Lowcountry Jobs Now postings found within the safety cap of 500 jobs / 50 pages. This will upsert external jobs only and will not delete or stale existing jobs.
          </p>
          {syncCleanup && (
            <div className="p-2 bg-red-50 rounded border border-red-200 text-xs text-red-700 mb-3">
              <p className="font-semibold">Cleanup is also enabled.</p>
              <p>Type cleanup phrase: <code className="bg-red-100 px-1">DELETE DEMO EMPLOYER DATA EXCEPT RICHY</code></p>
              <Input data-testid="sync-full-cleanup-input" value={syncConfirmPhrase} onChange={(e) => setSyncConfirmPhrase(e.target.value)} placeholder="Cleanup phrase..." className="mt-1" />
            </div>
          )}
          <p className="text-xs font-semibold text-slate-700 mb-2">Type exactly: <code className="bg-amber-50 px-1 py-0.5 text-amber-700">IMPORT LOWCOUNTRY JOBS</code></p>
          <Input data-testid="sync-full-confirm-input" value={syncFullConfirmPhrase} onChange={(e) => setSyncFullConfirmPhrase(e.target.value)} placeholder="Type confirmation phrase..." className="mb-3" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSyncFullConfirmOpen(false)}>Cancel</Button>
            <Button data-testid="sync-full-confirm-execute-btn" size="sm" onClick={handleSyncFullLive}
              disabled={syncFullConfirmPhrase !== 'IMPORT LOWCOUNTRY JOBS' || (syncCleanup && syncConfirmPhrase !== 'DELETE DEMO EMPLOYER DATA EXCEPT RICHY') || syncLoading}
              className="bg-amber-600 hover:bg-amber-700 text-white">
              {syncLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Import All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Section content — custom nav-driven (replaces the Radix Tabs strip) */}
      <div className="w-full">
        {adminTab === 'overview' && <AdminOverview analytics={analytics} />}
        {adminTab === 'organizations' && <AdminOrganizations />}
        {adminTab === 'activity' && (
          <div className="space-y-6" data-testid="admin-activity-section">
            <div className="flex gap-2" data-testid="activity-view-toggle">
              <button onClick={() => setActivityView('events')} data-testid="activity-view-events"
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${activityView === 'events' ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100 border border-slate-200'}`}>Recent Events</button>
              <button onClick={() => setActivityView('sessions')} data-testid="activity-view-sessions"
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${activityView === 'sessions' ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100 border border-slate-200'}`}>Session Explorer</button>
            </div>
            {activityView === 'sessions' ? <AdminSessions /> : <AdminActivity />}
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700">View legacy system / admin logs</summary>
              <div className="mt-3">
                <Card className="border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="text-left p-3 text-xs font-semibold text-slate-900">Timestamp</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-900">Action</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-900">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((log) => (
                          <tr key={log.log_id} className="border-b border-slate-100">
                            <td className="p-3 text-xs text-slate-600">{new Date(log.timestamp).toLocaleString()}</td>
                            <td className="p-3 text-xs text-slate-700">{log.action}</td>
                            <td className="p-3 text-xs text-slate-500">{log.details ? JSON.stringify(log.details) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            </details>
          </div>
        )}
        {adminTab === 'operations' && (
          <div className="space-y-4" data-testid="admin-operations">
            <Card className="p-5 border border-slate-200">
              <h3 className="text-sm font-bold text-slate-900 mb-1">SkillBridge Sync</h3>
              <p className="text-xs text-slate-500 mb-3">DoD SkillBridge acquisition &amp; sync engine (checkpointed, dry-run gated).</p>
              <Button size="sm" variant="outline" onClick={() => navigate('/admin/skillbridge-sync')} data-testid="ops-skillbridge-link">
                <RefreshCw className="w-4 h-4 mr-2" /> Open SkillBridge Sync
              </Button>
            </Card>
            <p className="text-xs text-slate-400">The External Job Sync controls are shown above.</p>
          </div>
        )}
        {adminTab === 'administration' && (
          <div className="space-y-4" data-testid="admin-administration">
            <Card className="p-5 border border-slate-200">
              <h3 className="text-sm font-bold text-slate-900 mb-1">Content</h3>
              <p className="text-xs text-slate-500 mb-3">Global News &amp; Employer Guides shown to employers across every region.</p>
              <Button size="sm" variant="outline" onClick={() => navigate('/admin/content')} data-testid="admin-content-btn">
                <FileText className="w-4 h-4 mr-2" /> Manage Content
              </Button>
            </Card>
            <Card className="p-5 border border-slate-200">
              <h3 className="text-sm font-bold text-slate-900 mb-1">Homepage Editor</h3>
              <p className="text-xs text-slate-500 mb-3">Manage public homepage content.</p>
              <Button size="sm" variant="outline" onClick={() => navigate('/admin/homepage-content')} data-testid="admin-homepage-editor-btn">
                <Home className="w-4 h-4 mr-2" /> Edit Homepage Content
              </Button>
            </Card>
            <Card className="p-5 border border-slate-200">
              <h3 className="text-sm font-bold text-slate-900 mb-1">Public Content</h3>
              <p className="text-xs text-slate-500 mb-3">Edit the public About, Contact, News &amp; Research, and legal pages.</p>
              <Button size="sm" variant="outline" onClick={() => navigate('/admin/public-content')} data-testid="admin-public-content-btn">
                <FileText className="w-4 h-4 mr-2" /> Manage Public Content
              </Button>
            </Card>
          </div>
        )}

        {/* Regions */}
        {adminTab === 'regions' && (
        <div>
          <div className="mb-4 flex flex-col sm:flex-row gap-4 justify-between">
            <div className="flex gap-4 flex-1">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search regions..."
                  value={searchRegion}
                  onChange={(e) => setSearchRegion(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowActiveOnly(!showActiveOnly)}
                className={showActiveOnly ? 'bg-brand text-white' : ''}
              >
                {showActiveOnly ? 'Active Only' : 'All Regions'}
              </Button>
            </div>
            <Button onClick={() => setCreateRegionOpen(true)} className="bg-brand hover:bg-brand-hover">
              <Plus className="w-4 h-4 mr-2" />
              Add Region
            </Button>
          </div>
          
          <Card className="border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Name</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Type</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">State</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Managers</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Employers</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Status</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {regions.map((region) => (
                    <tr key={region.region_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4 text-sm text-slate-900 font-medium">{region.name}</td>
                      <td className="p-4 text-sm text-slate-600">{region.type || 'County'}</td>
                      <td className="p-4 text-sm text-slate-600">{region.state || '-'}</td>
                      <td className="p-4 text-sm text-slate-600">{region.manager_count || 0}</td>
                      <td className="p-4 text-sm text-slate-600">{region.employer_count || 0}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          region.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {region.is_active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`edit-region-${region.region_id}`}
                            onClick={() => { setSelectedRegion({...region}); setEditRegionOpen(true); }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`toggle-region-${region.region_id}`}
                            onClick={() => handleToggleRegionActive(region)}
                          >
                            {region.is_active !== false ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`delete-region-${region.region_id}`}
                            onClick={() => handleDeleteRegion(region.region_id)}
                            className="text-red-600 hover:text-red-700"
                            disabled={(region.manager_count || 0) > 0 || (region.employer_count || 0) > 0}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
        )}

        {/* Users — unified experience (all users + Regional Manager management) */}
        {adminTab === 'users' && (
        <div className="space-y-8">
          <div data-testid="regional-managers-panel">
          <h3 className="text-base font-bold text-slate-900 mb-3">Regional Managers</h3>
          <div className="mb-4 flex justify-end">
            <Button onClick={() => setCreateManagerOpen(true)} className="bg-brand hover:bg-brand-hover">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Regional Manager
            </Button>
          </div>
          
          <Card className="border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Name</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Email</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Region</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Status</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {regionalManagers.map((manager) => (
                    <tr key={manager.admin_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4 text-sm text-slate-900 font-medium">{manager.name}</td>
                      <td className="p-4 text-sm text-slate-600">{manager.email}</td>
                      <td className="p-4 text-sm text-slate-600">
                        {manager.region_name}{manager.region_state && `, ${manager.region_state}`}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          manager.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {manager.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`edit-manager-${manager.admin_id}`}
                            onClick={() => { setSelectedManager({...manager}); setEditManagerOpen(true); }}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`reset-password-${manager.admin_id}`}
                            onClick={() => { setSelectedManager(manager); setResetPasswordOpen(true); }}
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`delete-manager-${manager.admin_id}`}
                            onClick={() => handleDeleteManager(manager.admin_id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {regionalManagers.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No regional managers yet. Click "Add Regional Manager" to create one.
                </div>
              )}
            </div>
          </Card>
          </div>

          <div data-testid="all-users-panel">
          <h3 className="text-base font-bold text-slate-900 mb-3">All Users</h3>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs text-slate-500">Filter by role</span>
            <select data-testid="users-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm">
              <option value="all">All roles</option>
              <option value="job_seeker">Job Seekers</option>
              <option value="employer">Employers</option>
              <option value="regional_admin">Regional Admins</option>
              <option value="admin">Admins</option>
            </select>
          </div>
          {/* Active / Trash toggle */}
          <div className="flex items-center gap-2 mb-4">
            <Button
              data-testid="users-active-tab"
              variant={usersView === 'active' ? 'default' : 'outline'}
              size="sm"
              className={usersView === 'active' ? 'bg-brand hover:bg-brand-hover text-white' : ''}
              onClick={() => setUsersView('active')}
            >
              <Users className="w-4 h-4 mr-1.5" /> Active Users
              <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-inherit">
                {users.filter(u => u.deleted_state !== 'trashed').length}
              </span>
            </Button>
            <Button
              data-testid="users-trash-tab"
              variant={usersView === 'trashed' ? 'default' : 'outline'}
              size="sm"
              className={usersView === 'trashed' ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-red-600 border-red-200 hover:bg-red-50'}
              onClick={() => setUsersView('trashed')}
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Trash
              {trashedUsers.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-inherit">
                  {trashedUsers.length}
                </span>
              )}
            </Button>
          </div>

          {usersView === 'active' ? (
            /* ── Active Users ── */
            <Card className="border border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-slate-900">Name</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-900">Email</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-900">Role</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-900">Status</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-900">Created</th>
                      <th className="text-right p-4 text-sm font-semibold text-slate-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter(u => u.deleted_state !== 'trashed').filter(u => roleFilter === 'all' || u.role === roleFilter).map((u) => (
                      <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-sm text-slate-900">
                          {u.name}
                          {u.role === 'regional_admin' && (
                            <span className="block text-xs text-slate-400">
                              {u.assigned_region?.region_name ? `Region: ${u.assigned_region.region_name}` : 'Unassigned'}
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-sm text-slate-600">{u.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.role === 'admin' ? 'bg-red-100 text-red-800' : 
                            u.role === 'regional_admin' ? 'bg-purple-100 text-purple-800' :
                            u.role === 'employer' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-brand-light text-brand'
                          }`}>{u.role}</span>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            u.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'
                          }`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                        </td>
                        <td className="p-4 text-sm text-slate-600">
                          {new Date(u.created_at).toLocaleDateString()}
                          {u.last_activity && (
                            <span className="block text-xs text-slate-400">Active {new Date(u.last_activity).toLocaleDateString()}</span>
                          )}
                        </td>
                        <td className="p-4 text-right whitespace-nowrap">
                          <Button
                            data-testid={`view-user-${u.user_id}`}
                            variant="ghost"
                            size="sm"
                            className="text-brand hover:bg-brand-light h-8 px-2 mr-1"
                            onClick={() => navigate(`/admin/users/${u.user_id}`)}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                          {u.role !== 'admin' && u.user_id !== user.user_id && (
                            <Button
                              data-testid={`trash-user-${u.user_id}`}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                              onClick={() => { setTargetUser(u); setTrashConfirmOpen(true); }}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Trash
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : (
            /* ── Trashed Users ── */
            <Card className="border border-red-200 bg-red-50/30">
              <div className="px-5 py-3 border-b border-red-200 bg-red-50 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <p className="text-sm text-red-700 font-medium">
                  Trashed users cannot log in. Restore or permanently delete.
                </p>
              </div>
              {trashedUsers.length === 0 ? (
                <div className="p-12 text-center">
                  <Trash2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">Trash is empty</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-red-50 border-b border-red-200">
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold text-red-900">Name</th>
                        <th className="text-left p-4 text-sm font-semibold text-red-900">Email</th>
                        <th className="text-left p-4 text-sm font-semibold text-red-900">Role</th>
                        <th className="text-left p-4 text-sm font-semibold text-red-900">Trashed At</th>
                        <th className="text-right p-4 text-sm font-semibold text-red-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trashedUsers.map((u) => (
                        <tr key={u.user_id} data-testid={`trashed-user-row-${u.user_id}`} className="border-b border-red-100 hover:bg-red-50/50">
                          <td className="p-4 text-sm text-slate-700">{u.name}</td>
                          <td className="p-4 text-sm text-slate-500">{u.email}</td>
                          <td className="p-4">
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">{u.role}</span>
                          </td>
                          <td className="p-4 text-sm text-slate-500 flex items-center gap-1.5">
                            <Clock className="w-3 h-3" />
                            {u.trashed_at ? new Date(u.trashed_at).toLocaleString() : '-'}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                data-testid={`restore-user-${u.user_id}`}
                                variant="outline"
                                size="sm"
                                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 h-8 px-3 text-xs"
                                onClick={() => handleRestoreUser(u)}
                                disabled={userActionLoading}
                              >
                                <RotateCcw className="w-3 h-3 mr-1" /> Restore
                              </Button>
                              <Button
                                data-testid={`purge-user-${u.user_id}`}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-800 hover:bg-red-100 h-8 px-3 text-xs font-semibold"
                                onClick={() => { setTargetUser(u); setPurgeConfirmText(''); setPurgeConfirmOpen(true); }}
                                disabled={userActionLoading}
                              >
                                Permanently Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}
          </div>
        </div>
        )}

        {adminTab === 'analytics' && <AdminAnalytics />}

        {/* Legacy activity logs retained (now surfaced under Activity); not rendered here */}
        {false && (
        <div>
          <Card className="border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Timestamp</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Action</th>
                    <th className="text-left p-4 text-sm font-semibold text-slate-900">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.log_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4 text-sm text-slate-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-brand text-white">
                          {log.action}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-slate-600">
                        {log.details ? JSON.stringify(log.details) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
        )}
      </div>

      {/* Create Region Dialog */}
      <Dialog open={createRegionOpen} onOpenChange={setCreateRegionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Region</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateRegion} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newRegion.name}
                onChange={(e) => setNewRegion({...newRegion, name: e.target.value})}
                placeholder="e.g., San Diego County"
                required
              />
            </div>
            <div>
              <Label>Type</Label>
              <select
                value={newRegion.type}
                onChange={(e) => setNewRegion({...newRegion, type: e.target.value})}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="City">City</option>
                <option value="County">County</option>
                <option value="Region">Region</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>State (2-letter code)</Label>
              <Input
                value={newRegion.state}
                onChange={(e) => setNewRegion({...newRegion, state: e.target.value.toUpperCase()})}
                placeholder="CA"
                maxLength={2}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                value={newRegion.description}
                onChange={(e) => setNewRegion({...newRegion, description: e.target.value})}
                placeholder="Optional description"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateRegionOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-brand hover:bg-brand-hover">Create Region</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Region Dialog */}
      <Dialog open={editRegionOpen} onOpenChange={setEditRegionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Region</DialogTitle>
          </DialogHeader>
          {selectedRegion && (
            <form onSubmit={handleUpdateRegion} className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={selectedRegion.name}
                  onChange={(e) => setSelectedRegion({...selectedRegion, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Type</Label>
                <select
                  value={selectedRegion.type || 'County'}
                  onChange={(e) => setSelectedRegion({...selectedRegion, type: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="City">City</option>
                  <option value="County">County</option>
                  <option value="Region">Region</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={selectedRegion.state || ''}
                  onChange={(e) => setSelectedRegion({...selectedRegion, state: e.target.value.toUpperCase()})}
                  maxLength={2}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={selectedRegion.description || ''}
                  onChange={(e) => setSelectedRegion({...selectedRegion, description: e.target.value})}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditRegionOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-brand hover:bg-brand-hover">Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Regional Manager Dialog */}
      <Dialog open={createManagerOpen} onOpenChange={setCreateManagerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Regional Manager</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateManager} className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={newManager.name}
                onChange={(e) => setNewManager({...newManager, name: e.target.value})}
                placeholder="John Doe"
                required
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newManager.email}
                onChange={(e) => setNewManager({...newManager, email: e.target.value})}
                placeholder="manager@example.com"
                required
              />
            </div>
            <div>
              <Label>Password *</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newManager.password}
                  onChange={(e) => setNewManager({...newManager, password: e.target.value})}
                  placeholder="Set initial password"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setNewManager({...newManager, password: generatePassword()})}
                >
                  Generate
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Share this password with the manager. They can login immediately.</p>
            </div>
            <div>
              <Label>Region *</Label>
              <select
                value={newManager.region_id}
                onChange={(e) => setNewManager({...newManager, region_id: e.target.value})}
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                required
              >
                <option value="">Select region...</option>
                {regions.filter(r => r.is_active !== false).map(r => (
                  <option key={r.region_id} value={r.region_id}>{r.name}{r.state && `, ${r.state}`}</option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateManagerOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-brand hover:bg-brand-hover">Create Manager</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Manager Dialog */}
      <Dialog open={editManagerOpen} onOpenChange={setEditManagerOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Regional Manager</DialogTitle>
          </DialogHeader>
          {selectedManager && (
            <form onSubmit={handleUpdateManager} className="space-y-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  value={selectedManager.name || ''}
                  onChange={(e) => setSelectedManager({...selectedManager, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Region</Label>
                <select
                  value={selectedManager.region_id || ''}
                  onChange={(e) => setSelectedManager({...selectedManager, region_id: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  {regions.map(r => (
                    <option key={r.region_id} value={r.region_id}>{r.name}{r.state && `, ${r.state}`}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedManager.is_active}
                  onChange={(e) => setSelectedManager({...selectedManager, is_active: e.target.checked})}
                />
                <Label>Active (can login)</Label>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditManagerOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-brand hover:bg-brand-hover">Save Changes</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-sm text-slate-600">
              Resetting password for: <strong>{selectedManager?.name}</strong> ({selectedManager?.email})
            </p>
            <div>
              <Label>New Password *</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setNewPassword(generatePassword())}
                >
                  Generate
                </Button>
              </div>
              <p className="text-xs text-amber-600 mt-1">Copy this password now. It will not be shown again.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setResetPasswordOpen(false); setNewPassword(''); }}>Cancel</Button>
              <Button type="submit" className="bg-brand hover:bg-brand-hover">Reset Password</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Trash User Confirmation ── */}
      <Dialog open={trashConfirmOpen} onOpenChange={setTrashConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Trash2 className="w-5 h-5 text-amber-500" /> Move to Trash
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-slate-600 mb-3">
              This will deactivate <span className="font-semibold text-slate-900">{targetUser?.email}</span> and block their access to the system.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <p className="text-xs text-amber-800">
                <strong>This is reversible.</strong> No data will be deleted. You can restore this user from the Trash tab at any time.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTrashConfirmOpen(false); setTargetUser(null); }} disabled={userActionLoading}>Cancel</Button>
            <Button
              data-testid="confirm-trash-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleTrashUser}
              disabled={userActionLoading}
            >
              {userActionLoading ? 'Moving...' : 'Move to Trash'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Permanently Delete Confirmation ── */}
      <Dialog open={purgeConfirmOpen} onOpenChange={(v) => { setPurgeConfirmOpen(v); if (!v) { setPurgeConfirmText(''); setTargetUser(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Permanently Delete User
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p className="text-sm text-slate-600 mb-3">
              You are about to permanently delete <span className="font-semibold text-red-700">{targetUser?.email}</span>.
            </p>
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">
              <p className="text-xs text-red-800 font-semibold mb-1">This cannot be undone. The following will be permanently removed:</p>
              <ul className="text-xs text-red-700 list-disc pl-4 space-y-0.5">
                <li>User account and profile</li>
                <li>All related business records (resumes, applications, jobs)</li>
                <li>Authentication credentials (including Supabase identity)</li>
                <li>Sessions and access tokens</li>
              </ul>
            </div>
            <Label className="text-sm text-slate-700 font-medium mb-2 block">
              Type <span className="font-mono bg-red-100 text-red-800 px-1.5 py-0.5 rounded text-xs">DELETE</span> to confirm:
            </Label>
            <Input
              data-testid="purge-confirm-input"
              value={purgeConfirmText}
              onChange={e => setPurgeConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="font-mono border-red-200 focus:border-red-400 focus:ring-red-200"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPurgeConfirmOpen(false); setPurgeConfirmText(''); setTargetUser(null); }} disabled={userActionLoading}>Cancel</Button>
            <Button
              data-testid="confirm-purge-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handlePurgeUser}
              disabled={purgeConfirmText !== 'DELETE' || userActionLoading}
            >
              {userActionLoading ? 'Deleting...' : 'Permanently Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Main Dashboard Component - Role-based routing
function Dashboard() {
  const [user, setUser] = useState(null);
  const [resume, setResume] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [nearDutyStation, setNearDutyStation] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [applications, setApplications] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        const userData = userRes.data;
        setUser(userData);
        
        // /dashboard is only for job_seekers and admins
        // Redirect other roles to their appropriate dashboard
        if (userData.role === 'employer') {
          console.log('[Dashboard] Employer detected, redirecting to /employer/dashboard');
          navigate('/employer/dashboard', { replace: true });
          return;
        }
        
        if (userData.role === 'regional_admin') {
          console.log('[Dashboard] Regional admin detected, redirecting to /regional-admin');
          navigate('/regional-admin', { replace: true });
          return;
        }
        
        // Job seekers: fetch resume data
        if (userData.role === 'job_seeker') {
          const resumeRes = await axios.get(`${BACKEND_URL}/api/resume`, { withCredentials: true });
          setResume(resumeRes.data);
          
          // Fetch jobs and unread count in parallel (non-blocking)
          fetchJobsAndActivity();
        }
        // Admin: no additional data needed, AdminDashboardContent fetches its own
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchJobsAndActivity = async () => {
      try {
        // Fetch jobs, unread count, conversations, and applications in
        // parallel. All non-blocking, all fail-soft — the dashboard must
        // never gate on these for state derivation. Conversations and
        // applications drive the State 4 "In motion" spine.
        const [jobsRes, unreadRes, convRes, appsRes, savedRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/jobs/recommended`, { withCredentials: true }).catch(() =>
            axios.get(`${BACKEND_URL}/api/jobs`).catch(() => ({ data: [] }))
          ),
          axios.get(`${BACKEND_URL}/api/messages/unread-count`, { withCredentials: true }).catch(() => ({ data: { count: 0 } })),
          axios.get(`${BACKEND_URL}/api/messages/conversations`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/applications/my-applications`, { withCredentials: true }).catch(() => ({ data: [] })),
          // Saved opportunities (native + external + SkillBridge) via the existing favorites list.
          axios.get(`${BACKEND_URL}/api/jobs/favorites/list`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        
        // /jobs/recommended returns { best_matches, near_duty_station }; the fail-soft
        // fallback (/api/jobs) returns a plain array → treat that as best_matches only.
        const recData = jobsRes.data;
        if (Array.isArray(recData)) {
          setJobs(recData);
          setNearDutyStation([]);
        } else {
          setJobs(recData?.best_matches || []);
          setNearDutyStation(recData?.near_duty_station || []);
        }
        setUnreadCount(unreadRes.data?.count || 0);
        setConversations(Array.isArray(convRes.data) ? convRes.data : []);
        setApplications(Array.isArray(appsRes.data) ? appsRes.data : []);
        setSavedJobs(Array.isArray(savedRes.data) ? savedRes.data : []);
      } catch (error) {
        console.error('Failed to fetch jobs/activity:', error);
      } finally {
        setLoadingJobs(false);
      }
    };

    fetchData();
  }, [navigate]);

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
        </div>
      </Layout>
    );
  }

  // Render based on role (employers/regional_admins already redirected)
  return (
    <Layout user={user}>
      {user?.role === 'admin' ? (
        <AdminDashboardContent user={user} />
      ) : (
        <JobSeekerDashboard 
          user={user} 
          resume={resume} 
          setResume={setResume} 
          navigate={navigate}
          jobs={jobs}
          nearDutyStation={nearDutyStation}
          unreadCount={unreadCount}
          conversations={conversations}
          applications={applications}
          savedJobs={savedJobs}
          loadingJobs={loadingJobs}
        />
      )}
    </Layout>
  );
}

export default Dashboard;
